import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import employeesApi from '../../api/employeesApi';
import factoryOptionsApi from '../../api/factoryOptionsApi';
import { FactoryOptionNameEnum, LoadingStatusEnum } from '../../constants/enums';
import { download } from '../../utils/files';

// ----------------------------------------------------------------------------

const REDUCER_NAME = 'employees';

export const CreateEmployee = createAsyncThunk<any, any>(`${REDUCER_NAME}/createEmployee`, async (data, thunkAPI) => {
  const res = await employeesApi.create(data);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetEmployees = createAsyncThunk(`${REDUCER_NAME}/getEmployees`, async (__, thunkAPI) => {
  const res = await employeesApi.get();

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetBirthdayHighlights = createAsyncThunk(`${REDUCER_NAME}/getBirthdayHighlights`, async (__, thunkAPI) => {
  const res = await employeesApi.getBirthdayHighlights();

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const UpdateEmployee = createAsyncThunk<any, any>(`${REDUCER_NAME}/updateEmployee`, async (data, thunkAPI) => {
  const res = await employeesApi.update(data);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetEmployeePdf = createAsyncThunk<any, string>(`${REDUCER_NAME}/getEmployeePdf`, async (id, thunkAPI) => {
  const res = await employeesApi.getPdf(id);

  if (res.status === 200) {
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const fileName = res.headers['x-suggested-filename'];
    download(url, fileName);

    // return data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetEmployeeTypes = createAsyncThunk(`${REDUCER_NAME}/getEmployeeTypes`, async (__, thunkAPI) => {
  const res = await factoryOptionsApi.getByName(FactoryOptionNameEnum.EmployeeTypes);

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const CreateEmployeeType = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/createEmployeeType`,
  async (value, thunkAPI) => {
    const res = await factoryOptionsApi.create({ optionName: FactoryOptionNameEnum.EmployeeTypes, value });

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const ChangeEmployeeInactiveStatus = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/ChangeEmployeeInactiveStatus`,
  async ({ id, inactive }, thunkAPI) => {
    const res = await employeesApi.changeInactiveStatus(id, inactive);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

// state
const initialState = {
  list: [],
  questionsForNewEmployee: {
    list: [],
    loading: 'idle',
  },
  employeeTypes: {
    list: [],
    loading: 'idle',
  },
  birthdayHighlights: {
    today: [],
    weekend: [],
    loading: 'idle',
  },
  loading: 'idle',
};

// slice
export const employeesSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    ResetEmployeesState: () => initialState,
  },
  extraReducers: (builder) => {
    // ------------------GetEmployees-------------------------
    builder.addCase(GetEmployees.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(GetEmployees.fulfilled, (state, action) => ({
      ...state,
      ...action.payload.data,
      questionsForNewEmployee: {
        list: action.payload.data?.factoryOptions?.questionsForNewEmployee || [],
        loading: 'succeeded',
      },
      loading: 'succeeded',
    }));

    builder.addCase(GetEmployees.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------GetBirthdayHighlights-------------------------
    builder.addCase(GetBirthdayHighlights.pending, (state) => ({
      ...state,
      birthdayHighlights: {
        ...state.birthdayHighlights,
        loading: LoadingStatusEnum.Pending,
      },
    }));

    builder.addCase(GetBirthdayHighlights.fulfilled, (state, action) => ({
      ...state,
      birthdayHighlights: {
        today: action.payload.data?.today || [],
        weekend: action.payload.data?.weekend || [],
        loading: LoadingStatusEnum.Succeeded,
      },
    }));

    builder.addCase(GetBirthdayHighlights.rejected, (state) => ({
      ...state,
      birthdayHighlights: {
        ...state.birthdayHighlights,
        loading: LoadingStatusEnum.Failed,
      },
    }));

    // ------------------UpdateEmployee-------------------------
    // builder.addCase(UpdateEmployee.pending, (state) => ({
    //   ...state,
    //   loading: 'pending',
    // }));

    builder.addCase(UpdateEmployee.fulfilled, (state: any, action) => {
      const { data } = action.payload;

      return {
        ...state,
        list: state.list.map((employee: any) => {
          if (employee._id === data._id) {
            return data;
          }

          return employee;
        }),
        loading: 'succeeded',
      };
    });

    builder.addCase(UpdateEmployee.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));
    // ------------------CreateEmployee-------------------------
    // builder.addCase(CreateEmployee.pending, (state) => ({
    //   ...state,
    //   loading: 'pending',
    // }));

    builder.addCase(CreateEmployee.fulfilled, (state: any, action) => {
      const { employee: newEmployee, questionsForNewEmployee } = action.payload.data;

      return {
        ...state,
        list: [...state.list, newEmployee],
        questionsForNewEmployee: {
          list: questionsForNewEmployee,
          loading: 'succeeded',
        },
        loading: 'succeeded',
      };
    });
    builder.addCase(CreateEmployee.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));
    // ------------------GetEmployeeTypes-------------------------
    builder.addCase(GetEmployeeTypes.pending, (state: any) => ({
      ...state,
      employeeTypes: {
        ...state.options,
        loading: 'pending',
      },
    }));

    builder.addCase(GetEmployeeTypes.fulfilled, (state, action) => {
      const { data } = action.payload;

      return {
        ...state,
        employeeTypes: {
          list: data,
          loading: 'succeeded',
        },
      };
    });
    builder.addCase(GetEmployeeTypes.rejected, (state: any) => ({
      ...state,
      employeeTypes: {
        ...state.options,
        loading: 'failed',
      },
    }));
    // ------------------CreateEmployeeType-------------------------
    // builder.addCase(CreateEmployeeType.pending, (state) => ({
    //   ...state,
    //   employeeTypes: {
    //     ...state.employeeTypes,
    //     loading: 'pending',
    //   },
    // }));

    builder.addCase(CreateEmployeeType.fulfilled, (state: any, action) => {
      const { data } = action.payload;

      return {
        ...state,
        employeeTypes: {
          list: [...state.employeeTypes.list, data],
          loading: 'succeeded',
        },
      };
    });
    builder.addCase(CreateEmployeeType.rejected, (state) => ({
      ...state,
      employeeTypes: {
        ...state.employeeTypes,
        loading: 'failed',
      },
    }));

    // ------------------ChangeEmployeeInactiveStatus------------------
    builder.addCase(ChangeEmployeeInactiveStatus.pending, (state) => ({
      ...state,
      loading: LoadingStatusEnum.Pending,
    }));
    builder.addCase(ChangeEmployeeInactiveStatus.fulfilled, (state: any, action) => {
      const updatedList = state.list.map((employee: any) => {
        if (employee._id === action.payload.data._id) {
          return action.payload.data;
        }
        return employee;
      });
      return {
        ...state,
        list: updatedList,
        loading: LoadingStatusEnum.Succeeded,
      };
    });
    builder.addCase(ChangeEmployeeInactiveStatus.rejected, (state) => ({
      ...state,
      loading: LoadingStatusEnum.Failed,
    }));
  },
});

// Export Actions
// export const { } = userSlice.actions;

// Export Selectors
export const selectEmployeesList = (state) => state.employees.list;
export const selectEmployeesLoading = (state) => state.employees.loading;
export const selectEmployeesBirthdayHighlights = (state) => state.employees.birthdayHighlights;

export const selectEmployeeTypes = (state) => state.employees.employeeTypes;
export const selectQuestionsForNewEmployee = (state) => state.employees.questionsForNewEmployee;

export const { ResetEmployeesState } = employeesSlice.actions;

export default employeesSlice.reducer;
